import { EVENTS_ACCENT } from './nodes/eventsTheme'

/**
 * EventActionsPanel — an ordered list of custom-event names to fire, with an
 * add dropdown of the event graph's declared custom events and a remove ✕ per
 * row. Used for a transition's Actions (ENG-1062) and a state's On Enter /
 * On Exit (smStateEvents.js). Pure presentation: the caller owns the list and
 * where it is stored, and positions the panel through `style`.
 */
export function EventActionsPanel({ title, hint, actions = [], available = [], onChange, style, testId }) {
  const remove = (name) => onChange?.(actions.filter(a => a !== name))
  const add = (name) => { if (name && !actions.includes(name)) onChange?.([...actions, name]) }
  // Choosable = declared custom events not already in this list.
  const choosable = available.filter(n => !actions.includes(n))

  // Themed via --m-* tokens; the Events-family identity color is the functional
  // EVENTS_ACCENT constant (imported, not a hex literal) — see paletteEnforcement.
  return (
    <div
      data-testid={testId}
      onMouseDown={e => e.stopPropagation()}
      style={{
        width: 220, background: 'var(--m-panel)', border: '1px solid var(--m-line)',
        borderRadius: 'var(--m-radius)', boxShadow: 'var(--m-shadow-card)',
        fontFamily: 'var(--m-font)', overflow: 'hidden', boxSizing: 'border-box',
        ...style,
      }}
    >
      <div style={{
        background: 'var(--m-panel-hi)', padding: '6px 10px',
        color: EVENTS_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ fontSize: 12 }}>⚡</span> {title}
      </div>

      <div style={{ padding: '8px 10px' }}>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 9, marginBottom: 6 }}>
          {hint}
        </div>

        {actions.length === 0 && (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 6 }}>No actions.</div>
        )}

        {actions.map(name => (
          <div key={name} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 6, background: 'var(--m-node-bg)', border: '1px solid var(--m-line)',
            borderRadius: 'var(--m-radius-sm)', padding: '4px 7px', marginBottom: 4,
          }}>
            <span style={{ color: EVENTS_ACCENT, fontSize: 10, fontFamily: 'var(--m-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </span>
            <button
              onClick={() => remove(name)}
              title="Remove action"
              style={{
                background: 'transparent', border: 'none', color: 'var(--m-fg-2)',
                cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0,
              }}
            >✕</button>
          </div>
        ))}

        {choosable.length > 0 ? (
          <select
            value=""
            onChange={e => { add(e.target.value); e.target.value = '' }}
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: 2,
              background: 'var(--m-bg-graph)', border: '1px solid var(--m-line)',
              color: 'var(--m-accent)', padding: '4px 6px', borderRadius: 'var(--m-radius-sm)',
              fontSize: 10, fontFamily: 'var(--m-font)', outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="" disabled>+ Add action…</option>
            {choosable.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        ) : (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, marginTop: 4, lineHeight: 1.4 }}>
            {available.length === 0
              ? 'No custom events yet. Add a Custom Event node in the Event graph and name it.'
              : 'All custom events already added.'}
          </div>
        )}
      </div>
    </div>
  )
}
